/**
 * Browser file-download helpers.
 *
 * These exist so that a file-download action can be completed from more than
 * one place: the originating mutation (the happy path) AND the federated
 * step-up replay path, where the original caller has already given up on the
 * 403 and the replayed response would otherwise be discarded.
 */

/** Parse the filename from a `Content-Disposition` header, falling back. */
export function filenameFromContentDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = /filename="?([^"]+)"?/i.exec(header);
  return match?.[1] ?? fallback;
}

/**
 * Trigger a browser "save file" for the given blob via a transient
 * `<a download>` element.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Defer revocation so the click has a chance to start the download in all
    // browsers.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * If `response` is a file attachment (`Content-Disposition: attachment`), read
 * its body and trigger a browser download. Returns `true` when a download was
 * triggered, `false` otherwise.
 *
 * Used to complete file-download actions (e.g. the account data export ZIP)
 * that were replayed after a federated step-up redirect, where the response
 * body would otherwise be silently discarded.
 */
export async function maybeDownloadAttachment(
  response: Response,
  fallbackFilename = 'download',
): Promise<boolean> {
  const disposition = response.headers.get('content-disposition');
  if (!disposition || !/attachment/i.test(disposition)) return false;
  const blob = await response.blob();
  const filename = filenameFromContentDisposition(disposition, fallbackFilename);
  triggerBlobDownload(blob, filename);
  return true;
}
